const DB_NAME = 'reseye-cache-db';
const STORE_NAME = 'dashboards';
const DB_VERSION = 1;

let db = null;

/**
 * Initializes the IndexedDB database and creates the object store if needed.
 * Returns a promise that resolves with the database instance.
 * @returns {Promise<IDBDatabase>}
 */
const initDB = () => {
  return new Promise((resolve, reject) => {
    // If the database instance is already available, resolve with it.
    if (db) {
      return resolve(db);
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = (event) => {
      console.error('IndexedDB error:', event.target.error);
      reject('IndexedDB error: ' + event.target.error);
    };

    request.onsuccess = (event) => {
      db = event.target.result;
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = event.target.result;
      // Create the object store if it doesn't already exist.
      // We are not using a keyPath because we will provide the key (dashboardId) explicitly.
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        database.createObjectStore(STORE_NAME);
      }
    };
  });
};

/**
 * Saves or updates a dashboard in the IndexedDB.
 * @param {string} id The dashboard ID (the key).
 * @param {object} data The data object to store, e.g., { code, version, name }.
 * @returns {Promise<void>}
 */
export const saveDashboard = async (id, data) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.put(data, id);

    request.onsuccess = () => {
      resolve();
    };

    request.onerror = (event) => {
      console.error('Error saving dashboard to DB:', event.target.error);
      reject(event.target.error);
    };
  });
};

/**
 * Retrieves a dashboard from the IndexedDB by its ID.
 * @param {string} id The dashboard ID to retrieve.
 * @returns {Promise<object|undefined>}
 */
export const getDashboard = async (id) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(id);

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onerror = (event) => {
      console.error('Error getting dashboard from DB:', event.target.error);
      reject(event.target.error);
    };
  });
};

/**
 * Retrieves all dashboards from the IndexedDB.
 * Returns a promise that resolves with an array of objects, each containing id and data.
 * @returns {Promise<Array<{id: string, data: object}>>}
 */
export const getAllDashboards = async () => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.openCursor();
    const dashboards = [];

    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        dashboards.push({ id: cursor.key, ...cursor.value });
        cursor.continue();
      } else {
        resolve(dashboards);
      }
    };

    request.onerror = (event) => {
      console.error('Error getting all dashboards from DB:', event.target.error);
      reject(event.target.error);
    };
  });
};

/**
 * Deletes a dashboard from the IndexedDB by its ID.
 * @param {string} id The dashboard ID to delete.
 * @returns {Promise<void>}
 */
export const deleteDashboard = async (id) => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.delete(id);

    request.onsuccess = () => {
      resolve();
    };

    request.onerror = (event) => {
      console.error('Error deleting dashboard from DB:', event.target.error);
      reject(event.target.error);
    };
  });
};

/**
 * Clears all dashboards from the object store.
 * @returns {Promise<void>}
 */
export const clearAllDashboards = async () => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.clear();

    request.onsuccess = () => {
      resolve();
    };

    request.onerror = (event) => {
      console.error('Error clearing all dashboards from DB:', event.target.error);
      reject(event.target.error);
    };
  });
};